import { demoFunc } from './dees-harness-message.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  state,
  cssManager,
  unsafeHTML,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { harnessActivityStyles } from '../harness.styles.js';
import { syntaxPaletteStyles, syntaxScopeStyles } from '../../00syntax.js';
import type { HLJSApi } from 'highlight.js';
import type {
  IHarnessAttachment,
  IHarnessMessage,
  IHarnessMessageViewState,
  IHarnessUsage,
  THarnessMessageStreamDelta,
} from '../interfaces.js';
import { DeesHarnessToolRegistry } from '../toolregistry.js';
import { HarnessMarkdown, markdownStyles } from '../harness.markdown.js';
import {
  harnessFormatBytes,
  harnessFormatClockTime,
  harnessRendersToolRow,
} from '../harness.helpers.js';
import { DeesServiceLibLoader } from '../../../services/index.js';
import '../dees-harness-reasoning/dees-harness-reasoning.js';
import { DeesHarnessToolCard } from '../dees-harness-tool-card/dees-harness-tool-card.js';
import '../../00group-utility/dees-icon/dees-icon.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-harness-message': DeesHarnessMessage;
  }
}

const markdownParseThrottleMs = 150;

/**
 * One conversation message. The `message` property object is the source of
 * truth; the streaming fast path (`appendTextDelta` etc.) mutates that same
 * object and re-renders only this element, so a later wholesale property
 * re-assignment by the host is idempotent.
 */
@customElement('dees-harness-message')
export class DeesHarnessMessage extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Agent Chat'];

  @property({ attribute: false })
  accessor message: IHarnessMessage = { id: '', role: 'assistant', text: '', createdAt: 0 };

  @property({ type: Boolean })
  accessor showRole: boolean = true;

  @property({ type: Boolean })
  accessor showTimestamp: boolean = false;

  /** parse markdown during streaming (throttled); plain text otherwise */
  @property({ type: Boolean })
  accessor markdownWhileStreaming: boolean = true;

  @property({ attribute: false })
  accessor toolRegistry: DeesHarnessToolRegistry = DeesHarnessToolRegistry.default;

  @property({ attribute: false })
  accessor viewState: IHarnessMessageViewState | undefined = undefined;

  @state()
  accessor renderedMarkdownHtml: string = '';

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    harnessActivityStyles,
    markdownStyles,
    css`
      :host {
        display: block;
        min-width: 0;
        max-width: 100%;
        /* A single unbreakable line (path, pattern, pill) clips at the card
           boundary instead of widening the whole conversation; scrollable
           payload blocks inside keep their own horizontal scrollbars. */
        overflow-x: clip;
        /* Virtualized rendering: cards outside the viewport skip style,
           layout, and paint entirely and re-render when scrolled back —
           state and DOM stay, so expansion and selection survive. The
           'auto' keyword keeps the last rendered size as the placeholder,
           holding the scrollbar steady. */
        content-visibility: auto;
        contain-intrinsic-size: auto 96px;
        font-family: var(--dees-font-family);
        overflow: clip;
      }

      /* A bare tool call is one 28px row until it is opened, not a paragraph of prose. */
      :host([data-tool-row]) {
        contain-intrinsic-size: auto 28px;
      }

      .message {
        padding: var(--dees-spacing-sm) var(--dees-spacing-md);
        border: 1px solid var(--dees-color-border-subtle);
        border-radius: var(--dees-radius-xl);
        corner-shape: var(--dees-corner-shape);
        background: var(--dees-color-bg-secondary);
      }

      .message.role-assistant {
        position: relative;
        padding: var(--dees-spacing-xs) var(--dees-spacing-md);
        border: 0;
        border-radius: 0;
        background: transparent;
      }

      .assistantActivityRail {
        position: absolute;
        inset: 9px auto auto 0;
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background: var(--dees-color-accent-primary);
        pointer-events: none;
      }

      .message.role-user {
        /* chat-like: user turns bind right and size to their content */
        border-color: transparent;
        background: var(--dees-color-fill-tertiary);
        margin-left: auto;
        width: fit-content;
        max-width: 78%;
      }

      .message.role-system {
        background: var(--dees-color-badge-warning-bg);
      }

      .message.has-error {
        border-color: var(--dees-color-accent-error);
      }

      .roleRow {
        display: flex;
        align-items: baseline;
        gap: var(--dees-spacing-sm);
        margin-bottom: var(--dees-spacing-xs);
      }

      .roleLabel {
        color: var(--dees-color-text-secondary);
        font-size: 11px;
        font-weight: 500;
        text-transform: capitalize;
      }

      .timestamp {
        color: var(--dees-color-text-muted);
        font-size: 11px;
        font-variant-numeric: tabular-nums;
      }

      /* Which model produced this message — trails the role/time so a
         mid-conversation model switch is visible in the transcript. */
      /* reasoning effort rides with the model as a subtle pill */
      .effortLabel {
        margin-left: 5px;
        padding: 0 5px;
        border-radius: var(--dees-radius-full);
        background: var(--dees-color-fill-secondary);
        color: var(--dees-color-text-secondary);
      }

      .modelLabel {
        margin-left: auto;
        color: var(--dees-color-text-muted);
        font-size: var(--dees-font-caption2-size, 10px);
        font-family: var(--dees-font-family-mono);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 45%;
      }

      .plainText {
        color: var(--dees-color-text-primary);
        white-space: pre-wrap;
        overflow-wrap: anywhere;
        font-size: var(--dees-font-control-size, 13px);
        line-height: 1.55;
      }

      .cursor {
        display: inline-block;
        width: 7px;
        height: 1.05em;
        margin-left: 1px;
        vertical-align: text-bottom;
        border-radius: 1px;
        background: var(--dees-color-accent-primary);
        will-change: opacity;
        animation: harness-cursor-blink 1.2s ease-in-out infinite;
      }

      @keyframes harness-cursor-blink {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.15; }
      }

      .reasoningStack {
        display: grid;
        gap: var(--dees-spacing-xs);
        margin-bottom: var(--dees-spacing-sm);
      }

      .attachments {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
        gap: var(--dees-spacing-sm);
        margin-bottom: var(--dees-spacing-sm);
      }

      .attachmentCard {
        min-width: 0;
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        padding: var(--dees-spacing-xs) var(--dees-spacing-sm);
        border: 1px solid var(--dees-color-border-subtle);
        border-radius: var(--dees-radius-md);
        corner-shape: var(--dees-corner-shape);
        background: var(--dees-color-bg-tertiary);
      }

      .attachmentThumb {
        width: 44px;
        height: 44px;
        flex: 0 0 44px;
        object-fit: cover;
        border-radius: var(--dees-radius-sm);
        background: var(--dees-color-bg-primary);
      }

      .attachmentIcon {
        width: 44px;
        height: 44px;
        flex: 0 0 44px;
        display: grid;
        place-items: center;
        border-radius: var(--dees-radius-sm);
        background: var(--dees-color-fill-secondary);
        color: var(--dees-color-accent-primary);
      }

      .attachmentInfo {
        min-width: 0;
      }

      .attachmentName {
        color: var(--dees-color-text-primary);
        font-size: var(--dees-font-control-size-sm, 12px);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .attachmentMeta {
        margin-top: 1px;
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption1-size, 11px);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .footerRow {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-md);
        margin-top: var(--dees-spacing-sm);
        color: var(--dees-color-text-muted);
        font-size: var(--dees-font-caption2-size, 10px);
      }

      .errorText {
        margin-top: var(--dees-spacing-sm);
        padding: var(--dees-spacing-xs) var(--dees-spacing-sm);
        border-radius: var(--dees-radius-md);
        background: var(--dees-color-badge-error-bg);
        color: var(--dees-color-badge-error-fg);
        font-size: var(--dees-font-control-size-sm, 12px);
        white-space: pre-wrap;
        overflow-wrap: anywhere;
      }

    `,
    /* Shared system syntax palette + highlight.js scope mapping */
    syntaxPaletteStyles,
    syntaxScopeStyles,
  ];

  private markdownParseToken = 0;
  private lastParsedText = '';
  private markdownThrottleTimeout: ReturnType<typeof setTimeout> | null = null;
  private highlightJs: HLJSApi | null = null;

  public get markdownActive(): boolean {
    return this.message.markdown ?? this.message.role === 'assistant';
  }

  // ------------------------------------------------------ streaming fast path

  /** Appends a text delta, mutating the shared message object. */
  public appendTextDelta(delta: string): void {
    this.message.text += delta;
    this.message.streaming = true;
    this.requestUpdate();
  }

  /** Appends to (or creates) a reasoning part by id. */
  public appendReasoningDelta(partId: string, delta: string, startedAt?: number): void {
    this.message.reasoning ??= [];
    let part = this.message.reasoning.find((candidate) => candidate.id === partId);
    if (!part) {
      part = { id: partId, text: '', startedAt: startedAt ?? Date.now() };
      this.message.reasoning.push(part);
    }
    part.text += delta;
    this.requestUpdate();
    // reasoning parts render as child elements holding the same part object
    for (const reasoningElement of this.shadowRoot?.querySelectorAll('dees-harness-reasoning') ?? []) {
      reasoningElement.requestUpdate();
    }
  }

  /** Marks a reasoning part as ended. */
  public endReasoning(partId: string, endedAt?: number): void {
    const part = this.message.reasoning?.find((candidate) => candidate.id === partId);
    if (part && !part.endedAt) {
      part.endedAt = endedAt ?? Date.now();
      this.requestUpdate();
      for (const reasoningElement of this.shadowRoot?.querySelectorAll('dees-harness-reasoning') ?? []) {
        reasoningElement.requestUpdate();
      }
    }
  }

  /** Ends the stream: final markdown parse, usage/error attach. */
  public endStream(usage?: IHarnessUsage, error?: string): void {
    this.message.streaming = false;
    if (usage) this.message.usage = usage;
    if (error) this.message.error = error;
    this.message.updatedAt = Date.now();
    this.requestUpdate();
  }

  /** Re-renders this message after its host mutates the shared message object in place. */
  public refreshMessage(): void {
    this.requestUpdate();
    for (const reasoningElement of this.shadowRoot?.querySelectorAll(
      'dees-harness-reasoning',
    ) ?? []) reasoningElement.requestUpdate();
    const toolCard = this.shadowRoot?.querySelector('dees-harness-tool-card');
    if (toolCard instanceof DeesHarnessToolCard) toolCard.refreshCall();
  }

  public applySubtaskDelta(delta: THarnessMessageStreamDelta): boolean {
    const card = this.shadowRoot?.querySelector('dees-harness-tool-card');
    return card?.applySubtaskDelta(delta) ?? false;
  }

  // ------------------------------------------------------------------ render

  public render(): TemplateResult {
    const message = this.message;
    // A pure tool message is the row itself; a message wrapper would frame it twice.
    if (harnessRendersToolRow(message)) {
      return html`
        <dees-harness-tool-card
          .call=${message.toolCall!}
          .toolRegistry=${this.toolRegistry}
          .markdownWhileStreaming=${this.markdownWhileStreaming}
          .showTimestamp=${this.showTimestamp}
          .viewState=${this.viewState}
        ></dees-harness-tool-card>
      `;
    }
    const roleClass = `role-${message.role}`;
    return html`
      <div class="message ${roleClass} ${message.streaming ? 'streaming' : ''} ${message.error ? 'has-error' : ''}">
        ${message.role === 'assistant' && message.streaming
          ? html`<span class="assistantActivityRail harnessActivity" aria-hidden="true"></span>`
          : ''}
        ${this.showRole || this.showTimestamp || message.model
          ? html`
              <div class="roleRow">
                ${this.showRole ? html`<span class="roleLabel">${message.role}</span>` : ''}
                ${this.showTimestamp && message.createdAt
                  ? html`<span class="timestamp">${harnessFormatClockTime(message.createdAt)}</span>`
                  : ''}
                ${message.model
                  ? html`
                      <span
                        class="modelLabel"
                        title=${message.effort ? `${message.model} · ${message.effort}` : message.model}
                      >${message.model}${message.effort
                        ? html`<span class="effortLabel">${message.effort}</span>`
                        : ''}</span>
                    `
                  : ''}
              </div>
            `
          : ''}
        ${message.reasoning?.length
          ? html`
              <div class="reasoningStack">
                <dees-harness-reasoning
                  .reasoningParts=${message.reasoning}
                  .viewState=${this.viewState}
                ></dees-harness-reasoning>
              </div>
            `
          : ''}
        ${message.attachments?.length ? this.renderAttachments(message.attachments) : ''}
        ${message.toolCall
          ? html`<dees-harness-tool-card
              .call=${message.toolCall}
              .toolRegistry=${this.toolRegistry}
              .markdownWhileStreaming=${this.markdownWhileStreaming}
              .showTimestamp=${this.showTimestamp}
              .viewState=${this.viewState}
            ></dees-harness-tool-card>`
          : ''}
        ${this.renderText()}
        ${message.error ? html`<div class="errorText">${message.error}</div>` : ''}
        ${message.usage?.totalTokens
          ? html`
              <div class="footerRow">
                <span>${message.usage.inputTokens ?? 0} in · ${message.usage.outputTokens ?? 0} out · ${message.usage.totalTokens} total tokens</span>
              </div>
            `
          : ''}
      </div>
    `;
  }

  private renderText(): TemplateResult {
    const message = this.message;
    if (!message.text) {
      return message.streaming ? html`<div class="plainText"><span class="cursor"></span></div>` : html``;
    }
    const markdownReady = this.markdownActive
      && this.renderedMarkdownHtml
      && (!message.streaming || this.markdownWhileStreaming)
      && this.lastParsedText.length > 0;
    if (markdownReady) {
      return html`
        <div class="markdown-body">${unsafeHTML(this.renderedMarkdownHtml)}</div>
        ${message.streaming ? html`<span class="cursor"></span>` : ''}
      `;
    }
    return html`
      <div class="plainText">${message.text}${message.streaming ? html`<span class="cursor"></span>` : ''}</div>
    `;
  }

  private renderAttachments(attachments: IHarnessAttachment[]): TemplateResult {
    return html`
      <div class="attachments">
        ${attachments.map((attachment) => {
          const imageUrl = attachment.kind === 'image' && attachment.dataBase64
            ? `data:${attachment.mediaType};base64,${attachment.dataBase64}`
            : '';
          return html`
            <div class="attachmentCard">
              ${imageUrl
                ? html`<img class="attachmentThumb" src=${imageUrl} alt=${attachment.name} />`
                : html`<div class="attachmentIcon"><dees-icon icon="lucide:FileText" iconSize="18"></dees-icon></div>`}
              <div class="attachmentInfo">
                <div class="attachmentName" title=${attachment.name}>${attachment.name}</div>
                <div class="attachmentMeta">
                  ${attachment.mediaType || 'file'} · ${harnessFormatBytes(attachment.size)}${attachment.truncated ? ' · truncated' : ''}
                </div>
              </div>
            </div>
          `;
        })}
      </div>
    `;
  }

  // ---------------------------------------------------------------- markdown

  public async updated(changedProperties: Map<string, any>): Promise<void> {
    super.updated(changedProperties);
    if (!this.markdownActive || !this.message.text) return;
    if (
      this.viewState?.markdownText === this.message.text
      && this.viewState.markdownHtml
      && this.lastParsedText !== this.message.text
    ) {
      this.lastParsedText = this.message.text;
      this.renderedMarkdownHtml = this.viewState.markdownHtml;
      if (!this.message.streaming) {
        await this.updateComplete;
        await this.highlightCodeFences(this.markdownParseToken);
      }
      return;
    }
    if (this.message.text === this.lastParsedText) return;
    if (this.message.streaming) {
      if (!this.markdownWhileStreaming) return;
      this.scheduleThrottledParse();
      return;
    }
    await this.parseMarkdown();
  }

  private scheduleThrottledParse(): void {
    if (this.markdownThrottleTimeout) return;
    this.markdownThrottleTimeout = setTimeout(() => {
      this.markdownThrottleTimeout = null;
      void this.parseMarkdown();
    }, markdownParseThrottleMs);
  }

  private async parseMarkdown(): Promise<void> {
    const token = ++this.markdownParseToken;
    const textAtRequest = this.message.text;
    const htmlResult = await HarnessMarkdown.getInstance().renderToHtml(textAtRequest);
    if (token !== this.markdownParseToken) return;
    this.lastParsedText = textAtRequest;
    this.renderedMarkdownHtml = htmlResult;
    if (this.viewState) {
      this.viewState.markdownText = textAtRequest;
      this.viewState.markdownHtml = htmlResult;
    }
    if (!this.message.streaming) {
      await this.updateComplete;
      await this.highlightCodeFences(token);
    }
  }

  /** Lazy hljs pass over rendered fences; runs only after the final parse. */
  private async highlightCodeFences(token: number): Promise<void> {
    const codeElements = Array.from(
      this.shadowRoot?.querySelectorAll<HTMLElement>('.markdown-body pre code') ?? [],
    ).filter((codeElement) => !codeElement.dataset.highlighted);
    if (!codeElements.length) return;
    if (!this.highlightJs) {
      this.highlightJs = await DeesServiceLibLoader.getInstance().loadHighlightJs();
    }
    if (token !== this.markdownParseToken) return;
    for (const codeElement of codeElements) {
      const languageMatch = /language-([\w-]+)/.exec(codeElement.className);
      try {
        const highlighted = languageMatch
          ? this.highlightJs.highlight(codeElement.textContent ?? '', {
              language: languageMatch[1],
              ignoreIllegals: true,
            }).value
          : this.highlightJs.highlightAuto(codeElement.textContent ?? '').value;
        codeElement.innerHTML = highlighted;
        codeElement.dataset.highlighted = 'true';
      } catch {
        codeElement.dataset.highlighted = 'skip';
      }
    }
  }

  public async disconnectedCallback(): Promise<void> {
    this.markdownParseToken += 1;
    if (this.markdownThrottleTimeout) {
      clearTimeout(this.markdownThrottleTimeout);
      this.markdownThrottleTimeout = null;
    }
    await super.disconnectedCallback();
  }
}
